import _ from 'lodash';
import { CoreFetch } from '../../api';
import {
    Amount,
    CurrencyType,
    HL7Api,
    ListProviderHL7MessagesOrderEnum,
    ListSupportHL7MessagesOrderEnum,
    PayerApi,
    ProviderHL7Message,
    ProviderHL7SortType,
    SupportHL7Message,
    SupportHL7SortType,
} from '../../api/server';
import { extractHl7ContextPayload } from '../../services';

export interface IClaimHl7Summary {
    id: string;
    patientFirst?: string;
    patientLast?: string;
    patientDob?: string;
    fromDos?: string;
    controlNumber?: string;
    primaryPayerName?: string;
    chargeAmount?: Amount;
}

type Hl7Message = ProviderHL7Message | SupportHL7Message;

interface IClaimHl7SummaryDraft extends Omit<IClaimHl7Summary, 'primaryPayerName'> {
    primaryPayerId?: string;
}

const getPayload = (msg: Hl7Message): Record<string, any> | undefined => {
    const payload = extractHl7ContextPayload(msg as any);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return undefined;
    }
    return payload as Record<string, any>;
};

/** Prefer professional payload when present — same idea as ClaimEditorStore.determineActivePayload. */
const getPreferredPayload = (msg: Hl7Message): Record<string, any> | undefined => {
    const record = msg as Record<string, any>;
    const professional =
        record.providerProfessional ||
        record.supportProfessional ||
        record.professional;
    if (professional && typeof professional === 'object') {
        return professional;
    }
    return getPayload(msg);
};

const sumChargeAmount = (services: any[] | undefined): Amount | undefined => {
    if (!Array.isArray(services) || services.length === 0) {
        return undefined;
    }

    let total = 0;
    let currency: CurrencyType = CurrencyType.USD;
    let hasAmount = false;

    services.forEach(service => {
        const amount = service?.chargeAmount?.amount;
        if (amount == null || amount === '') {
            return;
        }
        const value = parseFloat(amount);
        if (Number.isNaN(value)) {
            return;
        }
        hasAmount = true;
        total += value;
        if (service.chargeAmount?.currency) {
            currency = service.chargeAmount.currency as CurrencyType;
        }
    });

    if (!hasAmount) {
        return undefined;
    }

    return {
        amount: total.toFixed(2),
        currency,
    };
};

const getLatestDos = (services: any[] | undefined): string | undefined => {
    if (!Array.isArray(services)) {
        return undefined;
    }
    const withDos = services.filter(
        s => s && typeof s === 'object' && typeof s.fromDateOfService === 'string' && s.fromDateOfService,
    );
    if (!withDos.length) {
        return undefined;
    }
    const sorted = _.orderBy(withDos, ['fromDateOfService'], ['desc']);
    return sorted[0]?.fromDateOfService;
};

const toSummary = (claimId: string, msg?: Hl7Message): IClaimHl7SummaryDraft => {
    if (!msg) {
        return { id: claimId };
    }

    const payload = getPreferredPayload(msg);
    const patient = payload?.patient;
    const services = payload?.services;
    const primarySubscriber = Array.isArray(payload?.subscribers) ? payload.subscribers[0] : undefined;

    return {
        id: claimId,
        patientFirst: patient?.first || undefined,
        patientLast: patient?.last || undefined,
        patientDob: patient?.dob || undefined,
        fromDos: getLatestDos(services),
        controlNumber: payload?.controlNumber || undefined,
        primaryPayerId: primarySubscriber?.payerId || undefined,
        chargeAmount: sumChargeAmount(services),
    };
};

const fetchLatestHl7ForClaim = async (
    claimId: string,
    isSupportApp: boolean,
): Promise<Hl7Message | undefined> => {
    const api = CoreFetch(HL7Api);
    const messages = isSupportApp
        ? await api.listSupportHL7Messages({
              filterClaimId: [`EQ:${claimId}`],
              filterActive: 'EQ:1',
              sort: [SupportHL7SortType.CREATED],
              order: [ListSupportHL7MessagesOrderEnum.DESCENDING],
              limit: 1,
          })
        : await api.listProviderHL7Messages({
              filterClaimId: [`EQ:${claimId}`],
              sort: [ProviderHL7SortType.CREATED],
              order: [ListProviderHL7MessagesOrderEnum.DESCENDING],
              limit: 1,
          });

    return messages?.[0];
};

const resolvePayerNames = async (
    drafts: IClaimHl7SummaryDraft[],
    isSupportApp: boolean,
): Promise<IClaimHl7Summary[]> => {
    const payerIds = _.uniq(_.compact(drafts.map(s => s.primaryPayerId)));
    if (!payerIds.length) {
        return drafts.map(({ primaryPayerId: _payerId, ...summary }) => summary);
    }

    try {
        const api = CoreFetch(PayerApi);
        const payers = isSupportApp
            ? await api.listSupportPayers({ filterId: payerIds.map(id => `EQ:${id}`) })
            : await api.listProviderPayers({ filterId: payerIds.map(id => `EQ:${id}`) });

        const nameById = new Map(
            (payers || []).map(p => [p.id || '', p.nickname || p.name] as const),
        );

        return drafts.map(({ primaryPayerId, ...summary }) => ({
            ...summary,
            primaryPayerName: primaryPayerId ? nameById.get(primaryPayerId) || undefined : undefined,
        }));
    } catch (error) {
        console.warn('Failed to resolve payer names for claim HL7 summaries:', error);
        return drafts.map(({ primaryPayerId: _payerId, ...summary }) => summary);
    }
};

/**
 * Load display fields for linked claims from the latest HL7 by created date
 * (same source of truth as advanced search / claim editor), not from GET /claim.
 */
export const fetchClaimSummariesFromLatestHl7 = async (
    claimIds: string[],
    isSupportApp: boolean,
): Promise<IClaimHl7Summary[]> => {
    if (!claimIds.length) {
        return [];
    }

    const uniqueIds = _.uniq(claimIds.filter(Boolean));
    const latestByClaimId = new Map<string, Hl7Message | undefined>();

    await Promise.all(
        uniqueIds.map(async claimId => {
            try {
                latestByClaimId.set(claimId, await fetchLatestHl7ForClaim(claimId, isSupportApp));
            } catch (error) {
                console.error(`Failed to fetch HL7 for claim ${claimId}:`, error);
                latestByClaimId.set(claimId, undefined);
            }
        }),
    );

    const summaries = uniqueIds.map(claimId => toSummary(claimId, latestByClaimId.get(claimId)));
    return resolvePayerNames(summaries, isSupportApp);
};
